<template>
  <iframe
    ref="frame"
    :srcdoc="LISTENING_GLOW_HTML"
    aria-hidden="true"
    tabindex="-1"
    class="pointer-events-none absolute inset-0 size-full border-0 bg-transparent transition-opacity duration-200"
    :class="ready ? 'opacity-100' : 'opacity-0'"
    @load="handleLoad"
    @transitionend="handleFadeEnd"
  />
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'

import { LISTENING_GLOW_HTML } from './listeningGlowHtml'
import { toBase64 } from './pcm'
import { useChecker } from './useChecker'

interface GlowWindow extends Window {
  setDacrAnchor?: (x: number, y: number) => void
  setDacrListening?: (mode: string, reduced: boolean) => void
  setDacrPressed?: (pressed: boolean) => void
  pushDacrPcm?: (encoded: string, rate: number) => void
}

const props = defineProps<{
  anchor: HTMLElement | null
  pressed: boolean
  active: boolean
}>()

const emit = defineEmits<{ ready: []; failure: [] }>()

const checker = useChecker()
const frame = ref<HTMLIFrameElement>()
const loaded = ref(false)
const ready = ref(false)
const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches

let observer: ResizeObserver | null = null
let unsubscribe: (() => void) | null = null

const glow = () => frame.value?.contentWindow as GlowWindow | null | undefined

const place = () => {
  const element = frame.value
  const anchor = props.anchor
  if (!element || !anchor || !loaded.value) return
  const box = element.getBoundingClientRect()
  const target = anchor.getBoundingClientRect()
  if (!target.width) return
  glow()?.setDacrAnchor?.(
    target.left + target.width / 2 - box.left,
    target.top + target.height / 2 - box.top
  )
}

const mode = computed(() => (props.active ? checker.glowMode.value : 'idle'))

const applyMode = () => {
  if (!ready.value) return
  glow()?.setDacrListening?.(mode.value, reduced)
  unsubscribe?.()
  unsubscribe = null
  if (mode.value !== 'microphone') return
  unsubscribe = checker.subscribePcm((pcm, rate) =>
    glow()?.pushDacrPcm?.(toBase64(pcm), rate)
  )
}

const handleMessage = (event: MessageEvent) => {
  if (event.source !== glow()) return
  if (event.data === 'dacr-glow-ready') {
    ready.value = true
    applyMode()
  } else if (event.data === 'dacr-glow-error') emit('failure')
}

const handleFadeEnd = (event: TransitionEvent) => {
  if (event.propertyName === 'opacity' && ready.value) emit('ready')
}

const handleLoad = () => {
  loaded.value = true
  place()
}

watch(mode, applyMode)
watch(
  () => props.pressed,
  (pressed) => {
    if (ready.value) glow()?.setDacrPressed?.(pressed)
  }
)
watch(
  () => props.anchor,
  (anchor) => {
    observer?.disconnect()
    if (!anchor) return
    observer = new ResizeObserver(place)
    observer.observe(anchor)
    if (frame.value) observer.observe(frame.value)
    place()
  },
  { immediate: true }
)

onMounted(() => {
  window.addEventListener('message', handleMessage)
  window.addEventListener('resize', place)
})

onBeforeUnmount(() => {
  window.removeEventListener('message', handleMessage)
  window.removeEventListener('resize', place)
  observer?.disconnect()
  unsubscribe?.()
})
</script>
